import { useState } from "react";
import { ExternalLink, ChevronDown, ChevronUp, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ColourPalettePill from "@/components/common/ColourPalettePill";
import SaveButton from "@/components/products/SaveButton";
import ProductCardShell from "@/components/products/ProductCardShell";
import {
  cardLinkFor,
  cardProductFromProduct,
} from "@/components/products/card-product";
import type { Product } from "@shared/schema";
import { trackOutboundClick } from "@/lib/affiliate";
import { trackProductEvent } from "@/lib/product-events";
import { cn } from "@/lib/utils";
import JsonLd from "@/components/common/JsonLd";

/**
 * The shop's product card.
 *
 * The frame, the photographs and the words come from `ProductCardShell`, which
 * the Mum Hub draws its pieces on too. Everything below is the part that is
 * genuinely the shop's: structured data, the save button, Maria's pick, the
 * attribute badges and the button that goes to the merchant.
 *
 * The markup this produces is pinned character for character in
 * `__tests__/ProductCardMarkup.test.tsx`. Sharing the card with another
 * surface is exactly the change that alters the shop by accident.
 */

interface ProductCardProps {
  product: Product;
  compact?: boolean;
  /**
   * On a rail that is already filtered to the customer's own shape and
   * palette (My Hub's new arrivals), the description and the body type and
   * palette pills say nothing she has not already been promised, so they are
   * left off and the card is the photograph, the brand, the name and the price.
   */
  personalised?: boolean;
}

export default function ProductCard({ product, compact = false, personalised = false }: ProductCardProps) {
  const [isWhyOpen, setIsWhyOpen] = useState(false);
  const isMariasPick = (product as any).isMariasPick ?? (product as any).mariasPick ?? (product as any).isMariaPick ?? (product as any).mariaPick ?? (product as any).is_marias_pick ?? false;

  const card = cardProductFromProduct(product);
  // Non-null: `cardProductFromProduct` answers "#" rather than null for a
  // product with no URL, which is what the shop has always rendered.
  const link = cardLinkFor(card)!;
  const productUrl = link.href;

  const buttonBrand = ["paige", "good american"].includes(
    product.brand.toLowerCase()
  )
    ? "Reiss"
    : product.brand;

  return (
    <ProductCardShell
      images={card.images}
      altFor={(index) => `${product.brand} ${product.name} - image ${index + 1}`}
      brand={card.brand}
      name={card.name}
      priceLabel={card.priceLabel}
      originalPriceLabel={card.originalPriceLabel}
      compact={compact}
      head={
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          sku: product.slug,
          brand: { "@type": "Brand", name: product.brand },
          description: product.description || undefined,
          image: product.image,
          ...(product.category && { category: product.category }),
          offers: {
            "@type": "Offer",
            price: product.price?.toFixed(2),
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: productUrl,
            seller: {
              "@type": "Organization",
              name: product.brand,
            },
          },
        }} />
      }
      badge={
        isMariasPick ? (
          <div className={cn("absolute", compact ? "top-2 left-2" : "top-4 left-4")}>
            <Badge className={cn(
              "bg-soft-blush-ink text-white font-semibold",
              compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-3 py-1"
            )}>
              <Star className={cn("mr-1", compact ? "w-2.5 h-2.5" : "w-3 h-3")} fill="currentColor" />
              Maria's Pick
            </Badge>
          </div>
        ) : null
      }
      action={
        <SaveButton
          productId={product.id}
          productName={product.name}
          className={compact ? "top-2 right-2" : "top-4 right-4"}
        />
      }
    >
      {product.description && !compact && !personalised && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
          {product.description}
        </p>
      )}

      {!compact && (
        <>
          <div className="flex items-center justify-between mb-4">
            {product.whyThisPick && (
              <Collapsible open={isWhyOpen} onOpenChange={setIsWhyOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-sm text-soft-blush hover:text-dusty-rose font-medium p-0 h-auto"
                  >
                    Why this pick?
                    {isWhyOpen ? (
                      <ChevronUp className="ml-1 h-3 w-3" />
                    ) : (
                      <ChevronDown className="ml-1 h-3 w-3" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
            )}
          </div>

          {product.whyThisPick && (
            <Collapsible open={isWhyOpen} onOpenChange={setIsWhyOpen}>
              <CollapsibleContent className="mb-4">
                <div className="p-3 bg-muted rounded-md text-sm text-gray-600 dark:text-gray-300">
                  {product.whyThisPick}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}

      <div className="mt-auto">
        {/* Product attributes */}
        {!compact && !personalised && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.sustainability && (
              <Badge variant="secondary" className="text-xs">
                Sustainable
              </Badge>
            )}
            {(() => {
              const rawBodyType = (product as any).bodyType ?? (product as any).body_type;
              const bodyTypes = Array.isArray(rawBodyType)
                ? rawBodyType
                : rawBodyType
                  ? [rawBodyType]
                  : [];

              return bodyTypes.length > 0 && bodyTypes.map((type: string) => (
                <Badge key={type} variant="outline" className="text-xs">
                  {type}
                </Badge>
              ));
            })()}
            {(() => {
              const raw = (product as any).colourPalette ?? (product as any).colour_palette;
              const palettes = Array.isArray(raw)
                ? raw
                : raw
                  ? [raw]
                  : [];

              return palettes.length > 0 && palettes.map((palette: string) => (
                <ColourPalettePill key={palette} palette={palette} />
              ));
            })()}
            {product.materials?.map(material => (
              <Badge key={material} variant="outline" className="text-xs">
                {material}
              </Badge>
            ))}
          </div>
        )}

        <Button
          asChild
          variant="outline"
          size={compact ? "sm" : "default"}
          className={cn(
            "w-full border-soft-blush text-soft-blush hover:bg-soft-blush hover:text-white transition-colors duration-200 whitespace-normal h-auto min-h-10 py-2 text-center",
            compact && "text-xs min-h-9"
          )}
        >
          <a
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            className="inline-flex items-center justify-center gap-2"
            onClick={() => {
              // Pure side-effects: the anchor's default navigation proceeds
              // untouched (no preventDefault, nothing awaited).
              trackOutboundClick({
                name: product.name,
                brand: product.brand,
                slug: card.trackingSlug,
                url: card.url ?? undefined,
                href: productUrl,
                surface: "card",
              });
              trackProductEvent(product.id, "shop_click");
            }}
          >
            <span className="break-words">Shop at {buttonBrand}</span>
            <ExternalLink className={cn("shrink-0", compact ? "h-2.5 w-2.5" : "h-3 w-3")} />
          </a>
        </Button>
      </div>
    </ProductCardShell>
  );
}
